import { AfterViewChecked, Component, Input, QueryList, ViewChild, ViewChildren, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumber, InputNumberModule } from 'primeng/inputnumber';
import { TranslateModule } from '@ngx-translate/core';
import { DatePickerModule, DatePicker } from 'primeng/datepicker';

@Component({
  selector: 'rqa-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: true,
  imports: [DatePickerModule, ButtonModule, InputNumberModule, TranslateModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RqaCalendarComponent),
      multi: true
    }
  ]
})
export class RqaCalendarComponent implements ControlValueAccessor, AfterViewChecked {
  @ViewChild('calendar') calendar!: DatePicker;
  @ViewChildren(InputNumber) inputNumbers!: QueryList<InputNumber>;

  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  @Input() showTime: boolean = false;
  @Input() touchUI: boolean = false;
  @Input() dateFormat: string = 'yy-mm-dd';
  @Input() showIcon: boolean = true;
  @Input() iconDisplay: string = 'input';
  @Input() appendTo: string = 'body';
  @Input() hideOnDateTimeSelect: boolean = true;

  value: Date | null = null;
  disabled: boolean = false;

  private tempValue: Date | null = null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (_value: Date | null) => {};
  private onTouched = () => {};
  private autocompleteDisabled = false;

  ngAfterViewChecked() {
    if (!this.autocompleteDisabled && this.inputNumbers.length > 0) {
      this.inputNumbers.forEach((inputNumber) => {
        const inputEl = inputNumber.input?.nativeElement;
        if (inputEl) {
          inputEl.setAttribute('autocomplete', 'off');
        }
      });
      this.autocompleteDisabled = true;
    }
  }

  updateTime(value: number, type: 'hours' | 'minutes') {
    if (!this.value) {
      this.value = new Date();
    }

    const date = new Date(this.value);

    if (!isNaN(value)) {
      if (type === 'hours') {
        if (value >= 0 && value <= 23) {
          date.setHours(value);
        }
      } else {
        if (value >= 0 && value <= 59) {
          date.setMinutes(value);
        }
      }
      this.value = date;
      this.onChange(date);
    }
  }

  getHours(): number {
    if (this.value) {
      return this.value?.getHours() ?? 0;
    }
    return new Date().getHours();
  }

  getMinutes(): number {
    if (this.value) {
      return this.value?.getMinutes() ?? 0;
    }
    return new Date().getMinutes();
  }

  onModelChange(value: Date) {
    this.value = value;
    this.onChange(value);
  }

  // ControlValueAccessor implementation
  writeValue(value: Date): void {
    this.value = value;
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onCancelCalendar() {
    if (this.tempValue) {
      this.value = this.tempValue;
      this.onChange(this.tempValue);
    }
    this.calendar.hideOverlay();
  }

  onConfirmCalendar() {
    this.tempValue = this.value;
    this.calendar.hideOverlay();
  }
}
